import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Code2, Globe, ChevronDown, CheckCircle, Mail, MessageCircle,
  MapPin, Send, Clock, ShieldCheck, Zap
} from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import Navbar from './Navbar';
import Footer from './Footer';

const TelegramIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.499 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.692-1.653-1.12-2.678-1.795-1.185-.779-.416-1.207.258-1.908.177-.182 3.246-2.977 3.305-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.141-5.061 3.345-.479.329-.912.491-1.3.483-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.477-1.635.099-.002.321.023.465.14.121.099.155.232.169.326.014.094.028.27.014.37z" />
  </svg>
);

// --- Styled Components ---

const Nav = styled.nav`
  position: fixed;
  top: 0;
  width: 100%;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 5%;
  background: ${props => props.scrolled ? 'var(--nav-bg)' : 'transparent'};
  backdrop-filter: ${props => props.scrolled ? 'blur(12px)' : 'none'};
  border-bottom: ${props => props.scrolled ? '1px solid var(--glass-border)' : 'none'};
  transition: all 0.3s ease;
  z-index: 1000;
`;

const Logo = styled(Link)`
  font-family: 'Outfit', sans-serif;
  font-size: 1.5rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: var(--text-main);
  
  span {
    background: linear-gradient(to right, var(--primary), var(--secondary));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 30px;
  align-items: center;
  
  @media (max-width: 1024px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-muted);
  transition: color 0.3s;
  
  &:hover {
    color: var(--text-main);
  }
`;

const PageContainer = styled.div`
  padding-top: 120px;
  min-height: 100vh;
  background: var(--bg-dark);
  color: var(--text-main);
  overflow-x: hidden;
`;

const ContentSection = styled.section`
  padding: 80px 5%;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
`;

const MapContainer = styled.div`
  margin-top: 80px;
  width: 100%;
  height: 400px;
  border-radius: 32px;
  overflow: hidden;
  border: 1px solid var(--glass-border);
  background: var(--bg-card);

  iframe {
    width: 100%;
    height: 100%;
    border: 0;
    filter: invert(90%) hue-rotate(180deg) brightness(85%) contrast(85%);
  }
`;

const Title = styled(motion.h1)`
  font-size: clamp(2.5rem, 6vw, 4rem);
  margin-bottom: 20px;
  line-height: 1.1;
  text-align: center;
  
  span {
    background: linear-gradient(to right, var(--primary), var(--secondary));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.25rem;
  color: var(--text-muted);
  max-width: 800px;
  line-height: 1.6;
  margin: 0 auto 60px;
  text-align: center;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 60px;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const InfoPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const ContactCard = styled(motion.div)`
  padding: 30px;
  background: var(--bg-card);
  border: 1px solid var(--glass-border);
  border-radius: 24px;
  display: flex;
  align-items: flex-start;
  gap: 20px;
  transition: all 0.3s;

  &:hover {
    border-color: var(--primary);
    transform: translateX(10px);
  }

  .icon-box {
    width: 60px;
    height: 60px;
    background: rgba(99, 102, 241, 0.1);
    color: var(--primary);
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .content {
    h4 { margin-bottom: 5px; font-size: 1.1rem; }
    p { color: var(--text-muted); font-size: 0.95rem; line-height: 1.5; }
    a { color: var(--primary); text-decoration: none; font-weight: 500; }
  }
`;

const FormContainer = styled(motion.form)`
  padding: 50px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid var(--glass-border);
  border-radius: 32px;
  backdrop-filter: blur(20px);
  display: flex;
  flex-direction: column;
  gap: 24px;
  box-shadow: 0 30px 60px -12px rgba(0,0,0,0.4);

  @media (max-width: 768px) {
    padding: 30px;
  }
`;

const InputGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 18px 24px;
  background: var(--input-bg);
  border: 1px solid var(--glass-border);
  border-radius: 16px;
  color: var(--text-main);
  font-size: 1rem;
  transition: all 0.3s;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    background: var(--glass-bg);
    box-shadow: 0 0 0 1px var(--primary), 0 0 20px rgba(99, 102, 241, 0.2);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 18px 24px;
  background: var(--input-bg);
  border: 1px solid var(--glass-border);
  border-radius: 16px;
  color: var(--text-main);
  font-size: 1rem;
  min-height: 180px;
  resize: none;
  transition: all 0.3s;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    background: var(--glass-bg);
    box-shadow: 0 0 0 1px var(--primary), 0 0 20px rgba(99, 102, 241, 0.2);
  }
`;

const SubmitButton = styled(motion.button)`
  padding: 18px 40px;
  border-radius: 99px;
  border: none;
  background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
  color: white;
  font-weight: 700;
  font-size: 1.1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  box-shadow: 0 10px 30px -5px rgba(99, 102, 241, 0.5);
  margin-top: 10px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 15px 40px -5px rgba(99, 102, 241, 0.6);
  }
`;

const ResponseBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 24px;
  border: 1px solid rgba(34, 197, 94, 0.2);
`;

const LangContainer = styled.div`
  position: relative;
  margin-left: 20px;
`;

const LangButton = styled.button`
  background: transparent;
  border: 1px solid var(--glass-border);
  color: var(--text-main);
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  transition: all 0.3s;
  
  &:hover {
    border-color: var(--primary);
    background: rgba(255, 255, 255, 0.05);
  }
`;

const LangMenu = styled(motion.div)`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 10px;
  background: var(--bg-card);
  border: 1px solid var(--glass-border);
  border-radius: 12px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 120px;
  backdrop-filter: blur(12px);
  overflow: hidden;
`;

const LangItem = styled.button`
  background: transparent;
  border: none;
  color: var(--text-muted);
  padding: 8px 12px;
  text-align: left;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: white;
  }
  
  ${props => props.active && `
    background: rgba(99, 102, 241, 0.1);
    color: var(--primary);
  `}
`;

const Contact = () => {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [status, setStatus] = useState('idle'); // 'idle' | 'sending' | 'success' | 'error'
  const formRef = useRef();
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setLangOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');

    const formData = new FormData(formRef.current);
    const data = {
      user_name: formData.get('user_name'),
      user_email: formData.get('user_email'),
      subject: formData.get('subject'),
      message: formData.get('message'),
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setStatus('success');
        formRef.current.reset();
        setTimeout(() => setStatus('idle'), 5000);
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Contact error:', error);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  return (
    <>
      <Navbar />

      <PageContainer>
        <ContentSection>
          <Title
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span>Let's Connect</span> <br /> Scale Your Business
          </Title>
          <Subtitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {t('contact.sub')}
          </Subtitle>

          <ContactGrid>
            <InfoPanel>
              <ContactCard
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <div className="icon-box"><Mail /></div>
                <div className="content">
                  <h4>Email Us</h4>
                  <p>Our team is here to help.</p>
                  <a href="mailto:contact@nexovadev.com">contact@nexovadev.com</a>
                </div>
              </ContactCard>

              <ContactCard
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
              >
                <div className="icon-box"><TelegramIcon /></div>
                <div className="content">
                  <h4>Live Chat</h4>
                  <p>Telegram Support Channel</p>
                  <a href="https://t.me/nexova_dev" target="_blank" rel="noreferrer">@nexova_dev</a>
                </div>
              </ContactCard>

              <ContactCard
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
              >
                <div className="icon-box"><MapPin /></div>
                <div className="content">
                  <h4>Office</h4>
                  <p>{t('contact.address')}</p>
                </div>
              </ContactCard>

              <ContactCard
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
              >
                <div className="icon-box"><Clock /></div>
                <div className="content">
                  <h4>{t('contact.form.working')}</h4>
                  <p>{t('contact.form.hours')}</p>
                </div>
              </ContactCard>
            </InfoPanel>

            <FormContainer
              ref={formRef}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              onSubmit={handleSubmit}
            >
              <ResponseBadge>
                <Zap size={14} /> {t('contact.form.response')}
              </ResponseBadge>

              <h3 style={{ fontSize: '2rem', marginBottom: '8px' }}>{t('contact.form.title')}</h3>

              <InputGroup>
                <Input name="user_name" placeholder={t('contact.form.name')} required />
                <Input name="user_email" placeholder={t('contact.form.email')} type="email" required />
              </InputGroup>
              <Input name="subject" placeholder={t('contact.form.subject')} required />
              <TextArea name="message" placeholder={t('contact.form.message')} required />

              <SubmitButton
                type="submit"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={status === 'sending'}
              >
                {status === 'sending' ? 'Sending...' : (status === 'success' ? 'Sent!' : t('contact.form.send'))}
                {status === 'sending' ? null : (status === 'success' ? <CheckCircle size={20} /> : <Send size={20} />)}
              </SubmitButton>

              {status === 'success' && (
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ color: '#22c55e', textAlign: 'center', fontSize: '0.9rem' }}>
                  Your message has been sent successfully! We'll get back to you soon.
                </motion.p>
              )}
              {status === 'error' && (
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ color: '#f43f5e', textAlign: 'center', fontSize: '0.9rem' }}>
                  Something went wrong. Please try again or email us directly.
                </motion.p>
              )}

              <div style={{ display: 'flex', gap: '20px', marginTop: '20px', opacity: 0.6, fontSize: '0.85rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <ShieldCheck size={14} /> Secure Encryption
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <CheckCircle size={14} /> Global Compliance
                </div>
              </div>
            </FormContainer>
          </ContactGrid>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <MapContainer>
              <iframe
                src="https://maps.google.com/maps?q=30%20N%20Gould%20St,%20Sheridan,%20WY%2082801,%20USA&t=&z=14&ie=UTF8&iwloc=&output=embed"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Office Location"
              />
            </MapContainer>
          </motion.div>
        </ContentSection>
      </PageContainer>
      <Footer />
    </>
  );
};

export default Contact;
